/**
 * Viral attribution capture for Launch Pack referrals.
 *
 * On app boot, reads ?via= and ?from= from window.location.search and stashes
 * them in localStorage with a 30-day TTL. After auth completes, the consumer
 * calls writeAttributionIfPresent(userId) which writes to referral_attributions
 * (idempotent on (referred_user_id, attribution_event)).
 */
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "foxy_attribution_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredAttribution {
  via: string;
  from: string;
  captured_at: number;
}

// ── ARTIFACT SIGNUP ATTRIBUTION (2026-06-02) ────────────────────────────
// Reads ?mf_a=<artifact_id> from the URL on first load and stashes it
// (first-touch wins) so a signup can be attributed back to the artifact whose
// link the visitor followed. Same-origin localStorage survives the hop from a
// static marketing page into the React app. The writeback runs server-side in
// onboard-company complete_onboarding (additive-only).
const MF_ATTR_KEY = "mf_attribution";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function captureArtifactAttributionFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const id = new URLSearchParams(window.location.search).get("mf_a");
    if (!id || !UUID_RE.test(id)) return;
    if (localStorage.getItem(MF_ATTR_KEY)) return; // first-touch wins
    localStorage.setItem(MF_ATTR_KEY, JSON.stringify({ artifact_id: id, captured_at: Date.now() }));
  } catch {
    /* swallow */
  }
}

export function readArtifactAttribution(): { artifact_id: string; captured_at: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(MF_ATTR_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return (p && typeof p.artifact_id === "string" && UUID_RE.test(p.artifact_id)) ? p : null;
  } catch {
    return null;
  }
}

export function clearArtifactAttribution(): void {
  if (typeof window === "undefined") return;
  try { localStorage.removeItem(MF_ATTR_KEY); } catch { /* swallow */ }
}

export function captureAttributionFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const via = params.get("via");
    const from = params.get("from");
    if (!via) return;
    const stored: StoredAttribution = {
      via,
      from: from || "unknown",
      captured_at: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* swallow */
  }
}

export function readStoredAttribution(): StoredAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (Date.now() - parsed.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export async function writeAttributionIfPresent(userId: string, event: string = "signup"): Promise<void> {
  const stored = readStoredAttribution();
  if (!stored) return;
  try {
    await supabase
      .from("referral_attributions" as any)
      .insert({
        referrer_via_token: stored.via,
        referred_user_id: userId,
        source: stored.from,
        attribution_event: event,
        meta: { captured_at: new Date(stored.captured_at).toISOString() },
      });
    // Clear after first successful write so we don't double-count
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow — UNIQUE constraint will reject duplicates which is fine */
  }
}

/**
 * Resolve a stored via_token back to a referrer_user_id for display purposes.
 * via tokens are 8-char hashes derived from user_id, so we cannot reverse
 * without scanning. Caller should look up via launch_pack_user_plan first.
 */
export async function resolveReferrerFromVia(via: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from("launch_pack_user_plan" as any)
      .select("user_id")
      .eq("via_token", via)
      .maybeSingle();
    return (data as any)?.user_id || null;
  } catch {
    return null;
  }
}
